import React, { useMemo, useState, useContext } from 'react';

import { Icon } from 'design-system-web';
import { Button, Loading, ErrorMessage } from 'components';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { UserDataContext } from 'lane-shared/contexts';

import { useQuery } from '@apollo/client';
import { ExternalReference } from 'graphql-query-contracts';

import { routes } from 'lane-shared/config';
import { unitErrorCodeTypes } from 'activate-errors';
import { getUnitAndReferences, getUnit } from 'lane-shared/graphql/units';
import { getErrorCodes, getErrorMessagesFromCodes } from 'lane-shared/helpers';
// import { dates } from 'lane-shared/helpers/constants';
// import { dateFormatter } from 'lane-shared/helpers/formatters';
import { useFlag } from 'lane-shared/hooks';
import { ChannelExperienceTypeEnum } from 'constants-channel';
import { FeatureFlag } from 'constants-flags';
import hasPermission from 'lane-shared/helpers/hasPermission';
import { PERMISSION_KEYS } from 'constants-permissions';

import { Label } from 'components/general';
import { AdminPage, PageHeader } from 'components/layout';
import { H4, M } from 'components/typography';

import { ResidentsList } from '../../residents/ResidentsList';
import { EditUnit } from '../edit';
import { useUnitDeleteDisabledAlert } from '../hooks/useUnitDeleteDisabledAlert';

import styles from './styles.scss';
import { TenantsList } from '../../tenants/TenantsList';
import { useRemoveTenant } from '../hooks/useRemoveTenant';
import { getSharedTranslationKeys } from '../utils';
import { Channel } from 'packages/lane-shared/types/ChannelType';
import { Tenant } from 'lane-shared/types/Tenant';
import { Person } from 'packages/lane-shared/types/Person';

import { SourceTypeLabel } from '../../../../../../type-maps/source-type-label.map';

type Props = {
  channel: Channel;
};

const isDefined = <T,>(subject: T | undefined | null): subject is T => {
  return subject !== undefined && subject !== null;
};

export const UnitDetails = ({ channel }: Props) => {
  const isAddManualResidentsEnabled = useFlag(
    FeatureFlag.AddManualResidents,
    false
  );
  const isTenantManagementEnabled = useFlag(
    FeatureFlag.TenantManagement,
    false
  );
  const { t } = useTranslation();
  const { detailsPage } = getSharedTranslationKeys(channel.experienceType);
  const [edit, setEdit] = useState(false);
  const { unitId } = useParams<{ unitId: string }>();
  const { handleDeleteAlert } = useUnitDeleteDisabledAlert();
  const { handleRemove } = useRemoveTenant();
  const { user } = useContext(UserDataContext);
  const [isUnitUpdated, setIsUnitUpdated] = useState(false);
  const [removedTenants, setRemovedTenants] = useState<{
    [key: string]: boolean;
  }>({});

  const shouldDisplayExternalReference =
    user?.isSuperUser ||
    hasPermission(user?.roles, [PERMISSION_KEYS.PERMISSION_ADMIN], channel._id);

  const unitAndReferencesQuery = useQuery(getUnitAndReferences, {
    variables: {
      propertyId: channel._id,
      unitId,
    },
    skip: !shouldDisplayExternalReference,
  });

  const unitQuery = useQuery(getUnit, {
    variables: {
      propertyId: channel._id,
      unitId,
    },
    skip: shouldDisplayExternalReference,
  });

  const { data, error, loading, refetch } = shouldDisplayExternalReference
    ? unitAndReferencesQuery
    : unitQuery;

  const unit = data?.unit
    ? {
        ...data.unit,
        // This is required because of the query fork - data.unit is not typed correctly
        externalReferences: ((data.unit as any).externalReferences?.filter(
          isDefined
        ) ?? []) as ExternalReference[],
      }
    : undefined;

  if (isUnitUpdated) {
    refetch();
    setIsUnitUpdated(false);
  }

  // const lastUpdatedBy = unit?.updatedByUser?.name || unit?.createdByUser?.name;

  const getErrorMessage = (error: Error) => {
    const codes = getErrorCodes(error);
    const errorMessage = getErrorMessagesFromCodes(
      codes,
      unitErrorCodeTypes,
      t('shared.units.errors.generic')
    );

    return errorMessage;
  };

  const handleRemoveTenant = async (id: string, name: string) => {
    if (!unit) return;

    const isRemoved = await handleRemove({
      id,
      name,
      unitId,
      propertyId: channel._id,
      unitName: unit.name,
    });

    if (isRemoved) {
      setRemovedTenants(prevData => ({ ...prevData, [id]: true }));
    }
  };

  const tenantsToDisplay: Tenant[] = useMemo(() => {
    return (
      unit?.tenants?.filter(
        (tenant): tenant is Tenant =>
          !!tenant && !!tenant.id && !removedTenants[tenant.id]
      ) ?? []
    );
  }, [removedTenants, unit?.tenants]);

  const basePageHeaderProps = {
    header: unit?.name,
    headerLevel: 'h3' as 'h3',
    breadcrumbs: [
      {
        label: t(detailsPage.breadcrumbs.back),
        url: `${routes.channelAdminUnitsListView.replace(
          ':id',
          channel?.slug!
        )}`,
      },
      {
        label: t(detailsPage.breadcrumbs.current),
      },
    ],
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Loading />
      </div>
    );
  }

  if (!unit || error !== undefined) {
    return (
      <>
        <div>
          <PageHeader {...basePageHeaderProps} />
        </div>
        <ErrorMessage
          error={getErrorMessage(error ?? new Error('Unit Not Found'))}
          className={styles.errorMessage}
          fullWidth
        />
      </>
    );
  }

  // TODO: Add created / updated information back

  // const created = dateFormatter(
  //   unit?.createdAtDatetime!,
  //   dates.LONG_MONTH_DATE_YEAR
  // );

  // const updated = dateFormatter(
  //   unit?.updatedAtDatetime! || unit?.createdAtDatetime!,
  //   dates.LONG_DAY_MONTH_YEAR
  // );

  // const lastUpdatedInfo = lastUpdatedBy
  //   ? t('web.pages.portal.admin.units.details.lastUpdated', {
  //       lastUpdated: updated,
  //       updatedBy: lastUpdatedBy,
  //     })
  //   : t('web.pages.portal.admin.units.details.lastUpdatedNoUser', {
  //       lastUpdated: updated,
  //     });

  const fullPageHeaderProps = {
    ...basePageHeaderProps,
    // description: lastUpdatedInfo,
    actionButtons: [
      {
        label: t(detailsPage.deleteButton),
        type: 'secondary' as const,
        testId: 'deleteUnit',
        onClick: () => {
          if (!unit) return;

          handleDeleteAlert();
        },
      },
    ],
  };

  const isMFExperience =
    channel.experienceType === ChannelExperienceTypeEnum.multifamily;

  const isOfficeExperience =
    channel.experienceType === ChannelExperienceTypeEnum.office;

  const shouldDisplayResidentsList =
    isAddManualResidentsEnabled && isMFExperience;

  const shouldDisplayTenantsList =
    isTenantManagementEnabled && isOfficeExperience;

  let externalSourceType = '-';
  let externalSourceId = '-';

  if (
    shouldDisplayExternalReference &&
    'externalReferences' in unit &&
    !!unit.externalReferences
  ) {
    if (unit.externalReferences.length === 0) {
      externalSourceType = '-';
      externalSourceId = '-';
    } else {
      externalSourceType =
        unit.externalReferences?.[0]?.source.sourceType &&
        SourceTypeLabel[unit.externalReferences[0].source.sourceType]
          ? SourceTypeLabel[unit.externalReferences[0].source.sourceType]
          : '-';

      externalSourceId =
        unit.externalReferences?.[0]?.source.sourceEntityId || '-';
    }
  }

  return (
    <AdminPage className={styles.adminPage}>
      <div className={styles.pageHeader}>
        <PageHeader {...fullPageHeaderProps} />
      </div>
      {!edit ? (
        <>
          <div className={styles.unitDetails}>
            <div className={styles.header}>
              <H4>{t(detailsPage.header)}</H4>
              <Button
                startIcon={<Icon name="pencil" />}
                size="small"
                testId="editUnit"
                onClick={() => setEdit(true)}
              >
                {t('web.pages.portal.admin.units.details.edit')}
              </Button>
            </div>
            <div className={styles.body}>
              <div className={styles.unitFloorContainer}>
                <div className={styles.childWidth}>
                  <Label className={styles.label}>
                    {t(detailsPage.name.label)}
                  </Label>
                  <M>{unit?.name}</M>
                </div>
              </div>
              {shouldDisplayExternalReference && (
                <>
                  <div>
                    <Label className={styles.label}>
                      {t(
                        'web.pages.portal.admin.units.details.externalIdSource'
                      )}
                    </Label>
                    <M>{externalSourceType}</M>
                  </div>
                  <div>
                    <Label className={styles.label}>
                      {t('web.pages.portal.admin.units.details.externalId')}
                    </Label>
                    <M>{externalSourceId}</M>
                  </div>
                </>
              )}
              <div>
                <Label className={styles.label}>
                  {t('web.pages.portal.admin.units.details.description')}
                </Label>
                <M>{unit?.description || '-'}</M>
              </div>
              <div>
                <Label className={styles.label}>
                  {t('web.pages.portal.admin.units.details.floorNumber')}
                </Label>
                <M>
                  {unit?.floors
                    ?.filter(f => !!f)
                    .map(f => f?.name || f.index?.toString())
                    .join(', ') || '-'}
                </M>
              </div>
              {/* <div>
                    <Label className={styles.label}>
                      {t('web.pages.portal.admin.units.details.dateCreated')}
                    </Label>
                    <M>{created}</M>
                  </div> */}
            </div>
          </div>
          {shouldDisplayResidentsList && (
            <ResidentsList
              residents={(unit.people ?? []).filter((p): p is Person => !!p)}
            />
          )}
        </>
      ) : (
        <EditUnit
          channelId={channel._id}
          channelExperienceType={channel.experienceType}
          unit={unit}
          setEdit={setEdit}
          setIsUnitUpdated={setIsUnitUpdated}
        />
      )}
      {shouldDisplayTenantsList && (
        <TenantsList
          tenants={tenantsToDisplay}
          propertyChannelSlug={channel.slug}
          removeTenant={handleRemoveTenant}
        />
      )}
    </AdminPage>
  );
};
